import React, { useRef, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { OutTable, ExcelRenderer } from "react-excel-renderer";
import { useRecoilState, useRecoilValue } from "recoil";
import { userState, indicatorState } from "../../stores/atoms";
import { awsInstance } from "../../actions/api";

const LIMIT_EMPLOYEE = 1000;

function EmployeeRegister() {
  const [, setIndicator] = useRecoilState(indicatorState);
  const user = useRecoilValue(userState);
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);
  const fileInputRef = useRef();
  const fileButtonRef = useRef();
  const groupSelectRef = useRef();
  const history = useHistory();

  useEffect(() => {
    setIndicator("홈 > 직원 관리 > 직원 등록");

    async function getGroupList() {
      try {
        const { data } = await awsInstance.get("/business/group-list", {
          params: {
            id: user.company_id,
          },
        });

        if (data.data) {
          for (const group of data.data) {
            const option = document.createElement("option");
            option.value = group.id;
            option.textContent = group.name;
            groupSelectRef.current.append(option);
          }
        }
      } catch (e) {
        alert(e);
      }
    }
    getGroupList();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const headings = document.getElementsByClassName("heading");
    if (headings.length === 0) {
      return;
    }
    const trs = document.querySelectorAll("tr");
    // 첫 번째 번호가 0일 때 한 번만 실행
    if (headings[0].textContent === "0") {
      // 열 삭제해서 맞추기
      for (const tr of trs) {
        if (tr.lastElementChild.tagName === "TD") {
          tr.lastElementChild.remove();
        }
      }
      // 번호 1부터 시작
      for (const heading of headings) {
        heading.textContent++;
      }
    } else {
      // 실패인 경우 빨간색 처리
      for (const tr of trs) {
        if (tr.lastElementChild.textContent.substring(0, 2) === '실패') {
          tr.lastElementChild.setAttribute('style', 'color:red;');
        }
      }
    }
  }, [cols, rows]);

  const onExcelUpload = (event) => {
    const files = event.target.files;
    if (files.length === 0) {
      return;
    }
    const fileName = files[0].name;
    const lastDot = fileName.lastIndexOf('.') + 1;
    const fileExt = fileName.substring(lastDot, fileName.length).toLowerCase();
    if(['xls','xlsx'].indexOf(fileExt) === -1) {
      alert('등록할 수 없는 파일입니다.');
      fileInputRef.current.value = null;
      return;
    }
    //just pass the fileObj as parameter
    ExcelRenderer(files[0], async (err, resp) => {
      if (err) {
        alert(err);
        return;
      }
      const { cols, rows } = resp;
      if (rows.length - 1 > LIMIT_EMPLOYEE) {
        alert(`직원은 ${LIMIT_EMPLOYEE}명까지 등록 가능합니다.`);
        return;
      }
      const headers = [
        "번호",
        "이름",
        "휴대폰 번호",
        "이메일",
        "부서(선택)",
        "결과",
      ];
      for (let i = 0; i < headers.length; i++) {
        if (i < cols.length) {
          cols[i].name = headers[i];
        } else {
          cols.push({
            name: headers[i],
            key: i,
          });
        }
      }
      rows.shift();
      fileButtonRef.current.setAttribute("disabled", "disabled");
      setCols(cols);
      setRows(rows);
      for (const row of rows) {
        try {
          if (!row[3]) {
            row[3] = null;
          }
          const { data } = await awsInstance.post("/business/employee", {
            company_id: user.company_id,
            name: row[0],
            phone_number: row[1],
            email: row[2],
            department: row[3],
            business_group_id: groupSelectRef.current.value,
          });
          row.push(data.message);
        } catch (e) {
          if(row[1].toString().length < 11 || isNaN(row[1])) {
            row.push("실패: 휴대폰 번호가 정확하지 않습니다.");
          } else {
            row.push("실패: 서버 오류");
          }
        } finally {
          setRows(JSON.parse(JSON.stringify(rows)));
        }
      }
      fileInputRef.current.value = null;
      fileButtonRef.current.removeAttribute("disabled");
    });
  };

  return (
    <div className="employee-register">
      <h5>직원 등록</h5>
      <div className="display-flex justify-content-between align-items-center">
        <p className="txt-gray">
          양식에 맞추어 엑셀 업로드 해주세요. 엑셀 업로드 시 자동으로 직원을 등록하며 수 분 가량 소요될 수 있습니다.
          <br />
          그룹을 선택하면 선택한 그룹으로 직원이 등록되며,  입력한 이메일로 가입 메일이 발송됩니다.
          <br />
          한 번에 최대 1,000명까지 등록 가능합니다.
        </p>
      </div>
      <a href="./직원_등록_양식.xlsx" target="_blank" download>
        <button className="btn-blue p-2">양식 다운로드</button>
      </a>
      <div className="display-flex align-items-center mt-4">
        <span className="mr-3 p-0">그룹지정</span>
        <select className="form-control width-15 mr-5" ref={groupSelectRef}>
          <option className="font-bold" value={null}></option>
        </select>
        <input type="file" onChange={onExcelUpload} ref={fileInputRef} hidden />
        <button className="btn-white p-2" ref={fileButtonRef} onClick={() => fileInputRef.current.click()}>
          엑셀 업로드
        </button>
      </div>
      <div className="display-flex justify-content-center mt-4 overflow-y-auto height-40">
        <OutTable
          data={rows}
          columns={cols}
          tableClassName="table table-excel-data"
          tableHeaderRowClass="heading"
        />
      </div>
      <div className="display-flex justify-content-center">
        <button
          className="btn-white-md mr-4 mt-3"
          onClick={() => {history.goBack()}}>
          이전
        </button>
      </div>
    </div>
  );
}

export default EmployeeRegister;
